import { useCallback, useState, useEffect, useMemo } from 'react';
import { notification, Button, Space } from 'antd';
import { FormOutlined } from '@ant-design/icons';
import "./KiwiList.scss";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js"
import { withLocale } from '../../l10n/index.jsx';
import DataTable from '../DataTable/index.js';
import DeleteButton from '../DeleteButton/DeleteButton.jsx';
import withNavigation from '../hooks/withNavigation.jsx';
import Loading from "../../utils/loading.jsx";
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { formatDate, compareDates } from '../../utils/dates';
import FilterColumns from "../FilterColumns/index.js";
import { useGetVersionsLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function KiwiList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: true,
    selectedColumns: null,
  });

  const [data, setData] = useState({
    raw: [],
    selectedId: '',
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();

  // ===== 3. DERIVADOS (Computados) =====
  const versionFilter = versions
    .filter(v => v.device_type === 'kiwi')
    .map(v => ({ text: v.name, value: v.uuid }));

  // ===== 4. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    updateUI('loading', true);
    try {
      const res = await service.get('kiwi');
      if (res.data.ok === false) {
        notification["error"]({ message: res.data.message });
        updateData('raw', []);
      } else {
        updateData('raw', res.data.data || []);
      }
    } catch (err) {
      console.log('Error devuelto: ' + err);
      notification["error"]({
        message: lang.handle('Error al cargar los datos'),
      });
    } finally {
      updateUI('loading', false);
    }
  }, [lang]);

  const enrichedData = useMemo(() => {
    return data.raw.map((element) => {
      const version = versions.find(v => v.uuid === element.version_uuid);
      return {
        ...element,
        version_name: version ? version.name : lang.handle('Sin versión')
      };
    });
  }, [data.raw, versions, lang]);

  // ===== 6. HANDLERS =====
  const onDelete = async () => {
    updateUI('loading', true);
    try {
      const res = await service.del('kiwi/' + data.selectedId, data.selectedId)
      if (res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res.data.message),
        });
      } else {
        notification["success"]({
          message: lang.handle('Placa borrada correctamente'),
        });
      }
      await Pfetch();
    } catch (err) {
      console.log('Error devuelto: ' + err);
    } finally {
      updateData('selectedId', '');
      updateUI('loading', false);
    }
  };

  const onEdit = () => {
    navigate(`/boards/edit/2/${data.selectedId}`);
  };

  const onRowSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selectedId', selectedRows[0]?.uuid || '');
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    Pfetch();
  }, [Pfetch]);

  useEffect(() => {
    const saved = localStorage.getItem('kiwiColumns_visible');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          updateUI('selectedColumns', parsed);
        }
      } catch {
        localStorage.removeItem('kiwiColumns_visible');
      }
    }
  }, []);

  // ===== 8. CONFIG (columnas, etc) =====
  const allColumns = [
    {
      title: lang.handle('Pedido'),
      dataIndex: 'order_id',
      ...GetColumnLiteralSearchProps('order_id', lang),
    },
    {
      title: lang.handle('Número de serie'),
      dataIndex: 'serial_number',
      ...GetColumnLiteralSearchProps('serial_number', lang),
    },
    {
      title: lang.handle('Orden de trabajo'),
      dataIndex: 'work_order',
      ...GetColumnLiteralSearchProps('work_order', lang),
    },
    {
      title: 'SSID',
      dataIndex: 'ssid',
      ...GetColumnLiteralSearchProps('ssid', lang),
    },
    {
      title: 'MAC',
      dataIndex: 'mac',
      ...GetColumnLiteralSearchProps('mac', lang),
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: 'date',
      defaultSortOrder: 'descend',
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ['descend', 'ascend'],
      render: (data) => <p> {formatDate(data)} </p>
    },
    {
      title: lang.handle('Versión'),
      dataIndex: 'version_name',
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
      sorter: (a, b) => (a.version_name || "").localeCompare(b.version_name || ""),
    },
    {
      title: lang.handle('UUID placa'),
      dataIndex: 'board_uuid',
      ...GetColumnLiteralSearchProps('board_uuid', lang),
    },
    {
      title: lang.handle('Notas'),
      dataIndex: 'notes',
    },
    {
      title: lang.handle('Estado'),
      dataIndex: 'state',
      filters: [
        { text: lang.handle('Fabricado'), value: 'Fabricado' },
        { text: lang.handle('Asignado'), value: 'Asignado' },
        { text: lang.handle('Terminado'), value: 'Terminado' },
        { text: lang.handle('Eliminado'), value: 'Eliminado' },
      ],
      onFilter: (value, record) => record.state && record.state.toLowerCase() === value.toLowerCase()
    }
  ];

  let selection = null;
  if (hasPrivilege('administration_delete') || hasPrivilege('administration_edit')) {
    selection = {
      type: 'radio',
      selectedRowKeys: data.selectedId ? [data.selectedId] : [],
      onChange: onRowSelectionChange,
    };
  }

  const displayedColumns =
      (ui.selectedColumns === null || ui.selectedColumns.length === 0)
        ? allColumns
        : allColumns.filter(col => {
            const key = Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex;
            return ui.selectedColumns.includes(key);
          });

  const changeColumns = (newColumns) => {
      if (newColumns.length === 0) {
        updateUI('selectedColumns', null);
        localStorage.removeItem('kiwiColumns_visible');
      } else {
        const keys = newColumns.map(c => Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : c.dataIndex);
        updateUI('selectedColumns', keys);
        localStorage.setItem('kiwiColumns_visible', JSON.stringify(keys));
      }
    };

  // ===== 9. RENDER =====
  return (
    <div style={{ position: 'relative' }}>
      {ui.loading && <Loading loading={ui.loading} />}
      <div className="row">
        <div className="col">
        <FilterColumns
            columns={allColumns}
            selectedColumns={ui.selectedColumns === null ? [] : displayedColumns}
            changeColumns={changeColumns}
          />
        </div>
      </div>
      <DataTable
        rowSelection={selection}
        columns={displayedColumns}
        rowKey="uuid"
        dataSource={enrichedData}
        loading={false}
        scroll={displayedColumns.length > 5 ? { x: 'max-content' } : null}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('administration_delete') &&
            <DeleteButton
              onEvent={onDelete}
              buttonDisabled={!data.selectedId}
            />}

          {hasPrivilege('administration_edit') && <Button
            type="primary"
            icon={<FormOutlined />}
            disabled={!data.selectedId}
            onClick={onEdit}
          >
            {lang.handle('Editar')}
          </Button>}
        </Space>
      </div>
    </div>
  );
}

export default withLocale(withNavigation(KiwiList));
